import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, CircularProgress, FormControl, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { GridDeleteIcon } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ConfirmDialogList from '../../../../../components/ConfirmDialogList';
import configSiteUrls from '../../../configs/configSiteUrls';
import { ISacessoInformacao, ISacessoInformacaoList, AcessoInformacao, AcessoInformacaoList } from '../types/AcessoInformacao';
import { useDeleteAcessoInformacaoMutation, useGetAcessoInformacaosQuery } from '../acessoInformacaoApiSlice';
import { AcessoInformacaoDataTable } from './AcessoInformacaoDataTable';

export const AcessoInformacaoListForm = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [acessoInformacaos, setAcessoInformacaos] = useState<AcessoInformacaoList[]>([]);

  const [acessoInformacaoToDelete, setAcessoInformacaoToDelete] = useState<AcessoInformacaoList>(ISacessoInformacaoList);
  const [openConfirmExcluir, setOpenConfirmExcluir] = useState(false);
  const [deleteAcessoInformacao, { error: errorAcessoInformacao, isLoading, isSuccess }] = useDeleteAcessoInformacaoMutation();

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorPosition = useRef<number>(0);

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    cursorPosition.current = inputRef.current?.selectionStart || 0;
    setSearch(e.target.value);
  };

  useEffect(() => {
    // Limpar o timeout anterior
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Configurar o timeout para o debounce
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 700); // Tempo de debounce (700ms)

    // Limpar o timeout quando o componente for desmontado ou o efeito for reiniciado
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [search]); // Dependência do useEffect: quando o 'search' mudar

  const { data, isFetching, error } = useGetAcessoInformacaosQuery({
    search: debouncedSearch
  });

  useEffect(() => {
    if (data) {
      setAcessoInformacaos(data);
    }
    if (error) {
      enqueueSnackbar('Registros  não encontrados!', { variant: 'error' });
    } else if (isFetching) {
      enqueueSnackbar('Buscando registros...', { variant: 'info' });
    }

    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current?.setSelectionRange(cursorPosition.current, cursorPosition.current);
    }
  }, [data, error, isFetching, enqueueSnackbar]);

  const handleExcluirtClick = (rowData: any) => {
    setAcessoInformacaoToDelete(rowData);
    setOpenConfirmExcluir(true);
  };
  const handleCloseDialog = () => {
    setOpenConfirmExcluir(false);
  };
  const handleConfirm = async () => {
    setOpenConfirmExcluir(false);
    await deleteAcessoInformacao({
      id: acessoInformacaoToDelete.id ? acessoInformacaoToDelete.id : ''
    });
  };

  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar('Registro arquivado com sucesso!', { variant: 'success' });
      // fetchDados();
    }
    if (errorAcessoInformacao) {
      enqueueSnackbar('Registro não arquivado!', { variant: 'error' });
    }
  }, [isSuccess, errorAcessoInformacao]);

  if (isFetching || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <ConfirmDialogList
        open={openConfirmExcluir}
        handleClose={handleCloseDialog}
        title="Confirmar Ação"
        text={[
          `Você tem certeza de que deseja ${acessoInformacaoToDelete.arquivado ? 'desarquivar' : 'arquivar'} essa AcessoInformacao?`,
          acessoInformacaoToDelete.categoria ? `Categoria: ${acessoInformacaoToDelete.categoria}` : ''
        ]}
        handleDelete={() => {
          handleConfirm(); // Chama a função handleConfirm
          setOpenConfirmExcluir(false); // Fecha o diálogo
        }}
      />
      
      <Paper sx={{ width: '100%', height: '100vh', padding: '12px', display: 'flex', flexDirection: 'column' }}>
        <Grid container padding={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Box display="flex">
                  <Typography color="secondary" variant="h6" gutterBottom>
                    Registro de AcessoInformacaos
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>
                    Voltar
                  </Button>
                </Box>
              </Box>
            </FormControl>
          </Grid>

          <Grid item xs={12} mt={5}>
            <FormControl fullWidth>
              <TextField label="Digite para pesquisar" name="nome" onChange={handleChangeSearch} size="small" inputProps={{ maxLength: 100 }} value={search} inputRef={inputRef} />
            </FormControl>
          </Grid>
        </Grid>

        <AcessoInformacaoDataTable acessoInformacaos={acessoInformacaos} handleExcluirtClick={handleExcluirtClick} baseEditUrl={configSiteUrls.SITE_ACESSO_INFORMACAO_EDIT_LK} />
      </Paper>
    </>
  );
};
